import { useTranslation } from 'react-i18next';
import type { UploadedFile } from '@/mocks/dashboard';

interface Props {
  files: UploadedFile[];
}

const purposeIcons: Record<string, string> = {
  image_search: 'ri-search-line',
  quote_reference: 'ri-file-list-3-line',
  ai_finish_source: 'ri-magic-line',
  sample_reference: 'ri-test-tube-line',
  order_document: 'ri-file-text-line',
};

export default function DashboardFiles({ files }: Props) {
  const { t } = useTranslation('dashboard');

  return (
    <div className="rounded-xl border border-ink-100 bg-white">
      <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b border-ink-100">
        <div className="flex items-center gap-2">
          <i className="ri-folder-upload-line text-warm-400 text-lg" />
          <h3 className="text-sm md:text-base font-semibold text-ink-600">{t('dashboard.files.title')}</h3>
        </div>
        <a href="/my/files" className="text-xs md:text-sm font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap">
          {t('dashboard.seeAll')}
        </a>
      </div>

      {files.length === 0 ? (
        <div className="px-4 md:px-5 py-8 text-center">
          <div className="w-12 h-12 rounded-full bg-cream-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-folder-upload-line text-cream-400 text-xl" />
          </div>
          <p className="text-sm text-ink-300">{t('dashboard.files.empty')}</p>
        </div>
      ) : (
        <div className="divide-y divide-ink-100">
          {files.map((f) => (
            <div key={f.id} className="px-4 md:px-5 py-3 md:py-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1 flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-cream-100 flex items-center justify-center shrink-0">
                  <i className={`${purposeIcons[f.purpose] || 'ri-file-line'} text-ink-300 text-sm`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-ink-600 truncate">{f.fileName}</p>
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-ink-300 flex-wrap">
                    <span>{f.fileType.split('/').pop()?.toUpperCase()}</span>
                    <span className="text-ink-200">•</span>
                    <span>{t(`dashboard.filePurpose.${f.purpose}`)}</span>
                    {f.relatedNumber && (
                      <>
                        <span className="text-ink-200">•</span>
                        <span className="font-mono text-warm-400">{f.relatedNumber}</span>
                      </>
                    )}
                  </div>
                  <p className="text-[11px] text-ink-200 mt-0.5">{new Date(f.uploadedAt).toLocaleDateString()}</p>
                </div>
              </div>
              <a
                href={`/my/files/${f.id}`}
                className="shrink-0 text-xs font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap mt-1"
              >
                {t('dashboard.files.view')}
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}